import { useState } from "react";
import { useQuery, type QueryStatus } from "@tanstack/react-query";
import { Cpu, Leaf, Smartphone, Brush, Zap } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedContent } from "@/components/layout/locked-content";

import { cn, message } from "@/lib/utils";
import { BrandIntelApiFactory } from "@/apis/brand-os/brand-intel";
import type { KeyIndustryShift as KeyShiftType } from "@/schema/brand-os/brand-intel";

type IconStyle = { 
  Icon: LucideIcon; 
  className: string 
};

const shiftIconRules: { keywords: string[]; style: IconStyle }[] = [
  { keywords: ["sustain", "eco", "green", "recycl", "renew", "carbon", "climate", "environment", "clean", "conserv", "bio"], style: { Icon: Leaf, className: "bg-success/10 text-success" } },
  { keywords: ["social", "tiktok", "reel", "instagram", "youtube", "facebook", "linkedin", "pinterest", "short-form", "video", "platform", "media"], style: { Icon: Smartphone, className: "bg-blue-100/40 text-blue-600" } },
  { keywords: ["creator", "styling", "design", "influencer", "ugc", "brand", "content", "visual", "creative"], style: { Icon: Brush, className: "bg-violet-100/40 text-violet-600" } },
  { keywords: ["tech", "innovat", "ai", "intel", "artific", "machine", "learn", "comput", "chip", "memory", "gaming", "window", "tariff", "digital", "cloud", "data", "automat", "robot", "hardware", "software", "processor", "infra", "supply", "component", "pricing", "market"], style: { Icon: Cpu, className: "bg-warning/10 text-warning" } },
];

const defaultShiftIcon: IconStyle = { Icon: Zap, className: "bg-muted text-muted-foreground" };

function getShiftIconStyle(title: string): IconStyle {
  const lower = title.toLowerCase();
  return shiftIconRules.find((r) => r.keywords.some((k) => lower.includes(k)))?.style ?? defaultShiftIcon;
}

function ShiftDescription({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  const shouldClamp = text.length > 140;

  return (
    <div>
      <p className={cn("text-xs text-muted-foreground", !expanded && "line-clamp-2")}>{text}</p>
      {shouldClamp && (
        <button type="button" onClick={() => setExpanded((prev) => !prev)} className="mt-1 text-xs font-medium underline underline-offset-2 decoration-current">
          {expanded ? "Read less ↑" : "Read more →"}
        </button>
      )}
    </div>
  );
}

function IndustryShiftList({ shifts }: { shifts: KeyShiftType[] }) {
  if (!shifts.length) return <LockedContent message={message} className="mb-10" />;
  return (
    <ul className="space-y-4">
      {shifts.map((item, index) => {
        const { Icon, className } = getShiftIconStyle(item.title);
        return (
          <li key={index} className="flex items-center gap-4 py-3 first:pt-0">
            <div className={cn("flex size-10 shrink-0 items-center justify-center rounded-xl [&>svg]:size-5", className)}>
              <Icon strokeWidth={2.5} />
            </div>
            <div className="min-w-0 flex-1 space-y-0.5">
              <p className="font-medium text-foreground text-sm">{item.title}</p>
              <ShiftDescription text={item.description} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function IndustryShiftContent({ status, shifts }: { status: QueryStatus; shifts: KeyShiftType[] }) {
  switch (status) {
    case "pending":
      return <IndustryShiftSkeleton />;
    case "error":
      return <LockedContent message={message} className="mb-10" />;
    case "success":
      return <IndustryShiftList shifts={shifts} />;
  }
}

export function KeyIndustryShift() {
  const { data, status } = useQuery(BrandIntelApiFactory.Queries.FetchIndustryOverview());
  const shifts = (data?.key_industry_shifts ?? []).filter((item) => !!item.title?.trim() && !!item.description?.trim());

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-semibold">
          <div className="rounded-md bg-warning/10 p-2">
            <Zap className="size-5 text-warning" strokeWidth={2.5} />
          </div>
          Key Industry Shifts
        </CardTitle>
      </CardHeader>
      <CardContent className="overflow-y-auto max-h-100 scrollbar-hidden">
        <IndustryShiftContent status={status} shifts={shifts} />
      </CardContent>
    </Card>
  );
}

function IndustryShiftSkeleton() {
  return (
    <ul className="space-y-4">
      {Array.from({ length: 4 }, (_, i) => (
        <li key={i} className="flex items-center gap-4 py-3 first:pt-0">
          <Skeleton className="size-10 shrink-0 rounded-xl" />
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-full" />
          </div>
        </li>
      ))}
    </ul>
  );
}
